import {
  START_COLOR_DB_VFA_STANDARD,
  START_COLOR_DB_VFA_SWAP,
} from "@constants/testConstants/testSpecificConstants";
import {
  DYSCALCULIA_TASK_INSTRUCTION_VIDEOS,
  READING_ASSESSMENT_COMPREHENSION_INSTRUCTION_VIDEO,
} from "@constants/guideVideoConstants";
import {
  CompleteKineticPerimetryTestConfigPayload,
  CompleteNonVFATestConfigPayload,
  CompleteVFATestConfigPayload,
  ProgressConfigPayload,
  TestStartRequestPayload,
  TestSuiteSessionInfo,
} from "types/api/payloadTypes";
import { MessageOpcode } from "types/MessageOpcode";
import { ReadingAssessmentPageChangeInteraction } from "types/adminConfigurationTypes";
import { Patient } from "types/patientTypes";
import {
  ConfigExtraData,
  CustomCPTConfigLegacy,
  CustomDyslexiaConfigLegacy,
  DyscalculiaConfigPayload,
  OcularMotilityTestCustomConfigLegacy,
  PupillometryCustomConfigurationLegacy,
  ReadingAssessmentConfigPayload,
} from "types/tests/legacyTestTypes";
import { isFixationChanging } from "@utils/TestUtils";
import {
  ColorVisionCustomConfiguration,
  CPTConfigMode,
  CPTCustomConfiguration,
  DYCustomConfiguration,
  ReadingAssessmentCustomConfiguration,
  FDTTestCustomConfiguration,
  KineticTestCustomConfiguration,
  OMTestCustomConfiguration,
  PupillometryCustomConfiguration,
  TestCategory,
  TestConfiguration,
  TestLanguage,
  TestProgressConfiguration,
  TestSpecificConfig,
  VFABehaviour,
  VFATestCustomConfiguration,
  VirtualAssistantConfig,
  VisualAcuityCustomConfiguration,
  WaitDuration,
  EyeType,
} from "types/testTypes";
import {
  getFixationShiftAngle,
  getKineticPatternDefaults,
} from "@utils/test-specific-utils/KineticPerimetryUtils";
import { clampConfigurationNumber } from "@utils/configurationUtils";
import { supportsAssistantAudio } from "@constants/virtualAssistanceConstants";
import { DEFAULT_ADMIN_CONFIGURATIONS } from "@constants/adminConfigurationConstants";

const TESTS_WITH_PRESS_TO_START_DISABLED: ReadonlySet<TestCategory> = new Set([
  TestCategory.ContrastSensitivity,
]);

function resolveKineticTestTypeValues(extraData?: KineticTestCustomConfiguration | null) {
  const defaults = getKineticPatternDefaults(extraData?.testPattern);
  const testTypeCircleAngle = clampConfigurationNumber(
    extraData?.testTypeCircleAngle ?? defaults.testTypeCircleAngle,
    "testTypeCircleAngle",
  );
  const numberOfStimuliStarts = clampConfigurationNumber(
    extraData?.numberOfStimuliStarts ?? defaults.numberOfStimuliStarts,
    "numberOfStimuliStarts",
  );
  return { testTypeCircleAngle, numberOfStimuliStarts };
}

export function BuildTestStartPayload(
  opcode: MessageOpcode,
  pid: string,
  orgId: string,
  uuid: string,
  patient: Patient,
  age: number,
  config: TestConfiguration,
  actionStr: string,
  suiteSessionInfo?: TestSuiteSessionInfo,
  readingAssessmentPageChangeInteraction?: ReadingAssessmentPageChangeInteraction,
  headsetVersion?: string | null,
  movingFixationCatchTrialsEnabled?: boolean,
): TestStartRequestPayload {
  const envelope = {
    opcode,
    action: actionStr,
    pid,
    uuid,
    orgId,
    patient,
    age,
    suiteSessionInfo,
  };

  const mappedConfig =
    config.name === TestCategory.VisualField
      ? MapVFAConfigurationToPayload(config, headsetVersion, movingFixationCatchTrialsEnabled)
      : config.name === TestCategory.Kinetic
        ? MapKineticConfigurationToPayload(config)
        : MapNonVFATestConfigurationToPayload(config, readingAssessmentPageChangeInteraction);

  return {
    ...envelope,
    ...mappedConfig,
  };
}

export function BuildProgressConfigUpdatePayload(
  config: TestProgressConfiguration,
  testName: TestCategory,
  headsetVersion?: string | null,
  movingFixationCatchTrialsEnabled?: boolean,
): ProgressConfigPayload {
  const configTestName = config?.name || testName;
  if (configTestName === TestCategory.VisualField) {
    return {
      testConfig: buildVFAConfigPayload(config, headsetVersion, movingFixationCatchTrialsEnabled),
    };
  }
  return {
    progressConfig: MapNonVFATestConfigurationToPayload(config, undefined, configTestName),
  };
}

function buildVFAConfigPayload(
  config: TestConfiguration | TestProgressConfiguration,
  headsetVersion?: string | null,
  movingFixationCatchTrialsEnabled = DEFAULT_ADMIN_CONFIGURATIONS.visualFieldFixationLoss
    .movingFixationCatchTrials,
): CompleteVFATestConfigPayload["testConfig"] {
  const eyeConfiguration = config?.eyeConfiguration;
  const extraData = config?.extraData as VFATestCustomConfiguration;
  const virtualAssistanceConfig = config?.virtualAssistanceConfig;
  const assistantLanguage = supportsAssistantAudio(headsetVersion)
    ? virtualAssistanceConfig?.showAssistant
      ? virtualAssistanceConfig.assistantLanguage
      : null
    : config.voiceAssistantConfig?.isVoiceAssistantEnabled
      ? config.voiceAssistantConfig.assistantLanguage
      : null;

  let defaultMustWait: number | undefined;
  switch (config.testSpeed) {
    case WaitDuration.Standard:
      defaultMustWait = 400;
      break;
    case WaitDuration.Slow:
      defaultMustWait = 800;
      break;
    case WaitDuration.Slower:
      defaultMustWait = 1000;
      break;
  }

  const startColorDb =
    extraData?.testBehaviour === VFABehaviour.SWAP_TOP
      ? START_COLOR_DB_VFA_SWAP
      : START_COLOR_DB_VFA_STANDARD;

  const stimuliSize = extraData?.stimuliSize
    ? extraData.stimuliSize
    : extraData?.testBehaviour === VFABehaviour.SWAP_TOP
      ? 5
      : 3;

  return {
    defaultMustWait: defaultMustWait ?? 400,
    minAdaptiveWait: 400,
    maxAdaptiveWait: 500,
    totalFnCount: 10,
    totalFpCount: 10,
    startColorDb,
    minAcceptableFinishedNeighbourCount: 3,
    topReliabilityEnabled: extraData?.testBehaviour === VFABehaviour.ADAPTIVE_TRACKING,
    blindSpotMonitoringEnabled: extraData?.blindSpotMonitoringEnabled ?? false,
    fovealThresholdTestingEnabled: extraData?.fovealThresholdTestingEnabled ?? false,
    blindSpotTesting: extraData?.blindSpotTesting ?? false,
    autoPauseForGaze: isFixationChanging(config.testType)
      ? false
      : (eyeConfiguration?.autoPauseForGaze ?? false),
    colorCodeId: "",
    twoScreenFixation: eyeConfiguration?.isTwoScreenFixationEnabled ?? false,
    reLocateBlindSpot: false,
    movingFixationCatchTrialsEnabled,
    pauseTest: config?.pauseTest ?? false,
    enablePausingTheTestWithButton: false,
    fixationSize: extraData?.fixationSize!,
    stimuliSize: stimuliSize,
    sendActionMessages: false,
    voiceAssistantLanguage:
      assistantLanguage && assistantLanguage !== TestLanguage.NOT_SPECIFIED
        ? assistantLanguage
        : null,
    virtualAssistantConfig: (virtualAssistanceConfig ?? null) as VirtualAssistantConfig | null,
  };
}

function MapVFAConfigurationToPayload(
  config: TestConfiguration,
  headsetVersion?: string | null,
  movingFixationCatchTrialsEnabled?: boolean,
): CompleteVFATestConfigPayload {
  if (!config) {
    throw new Error("Test configuration is undefined or null");
  }
  if (config.name !== TestCategory.VisualField) {
    throw new Error("Provided configuration is not for a VFA test");
  }
  const eyeConfiguration = config?.eyeConfiguration;
  const testType = config?.testType;
  const extraData = config?.extraData as VFATestCustomConfiguration;

  let behaviour = VFABehaviour.SIMPLE_TEST;

  switch (extraData?.testBehaviour) {
    case VFABehaviour.TOP:
    case VFABehaviour.ADAPTIVE_TRACKING:
    case VFABehaviour.SITA_LIKE:
    case VFABehaviour.FULL_THRESH_4:
    case VFABehaviour.SWAP_TOP:
    case VFABehaviour.SIMPLE_TEST:
    case VFABehaviour.TWO_ZONE:
    case VFABehaviour.THREE_ZONE:
      behaviour = extraData.testBehaviour;
      break;
    default: {
      console.warn(
        "Unexpected VFA behaviour received while mapping VFA Configuration",
        extraData?.testBehaviour,
      );
    }
  }
  const eye = getEyeTypeForPayload(eyeConfiguration?.eyeType);

  return {
    behaviour: behaviour,
    testType: testType,
    showTutorial: false,
    testConfig: buildVFAConfigPayload(config, headsetVersion, movingFixationCatchTrialsEnabled),
    eye: eye,
    isBackToBackEyeTest: eyeConfiguration?.isBackToBackEyeTest ?? false,
    startWithCalibrationScene: config.startWithCalibrationScene ?? false,
    isPressToStartEnabled: config.isPressToStartEnabled,
    isEyeCalibrationNeeded: eyeConfiguration?.isCalibrationNeeded ?? true,
  };
}

function MapKineticConfigurationToPayload(
  config: TestConfiguration,
): CompleteKineticPerimetryTestConfigPayload {
  if (!config) {
    throw new Error("Test configuration is undefined or null");
  }
  if (config.name !== TestCategory.Kinetic) {
    throw new Error("Provided configuration is not for a Kinetic test");
  }
  const name = config.name as TestCategory.Kinetic;
  const kineticExtraData = (config?.extraData as KineticTestCustomConfiguration) ?? null;
  const { testTypeCircleAngle, numberOfStimuliStarts } =
    resolveKineticTestTypeValues(kineticExtraData);
  const fixationShiftAngle = getFixationShiftAngle(
    kineticExtraData?.testPattern,
    testTypeCircleAngle,
    numberOfStimuliStarts,
  );
  const testType = config?.testType;
  const testBehaviour = 6;
  const eye = config?.eyeConfiguration?.eyeType;
  const autoPauseForGaze = config?.eyeConfiguration?.autoPauseForGaze ?? false;
  const twoScreenFixation = config?.eyeConfiguration?.isTwoScreenFixationEnabled ?? false;
  const blindSpotTesting = false;
  const blindSpotMonitoringEnabled = false;
  const fovealThresholdTestingEnabled = false;
  const enablePausingTheTestWithButton = false;
  const defaultMustWait = 400;
  const showTutorial = false;
  const fixationSize = kineticExtraData?.fixationSize;
  const stimuliSize = kineticExtraData?.stimuliSize;
  const testPattern = kineticExtraData?.testPattern;
  const dbColor = kineticExtraData?.dbColor;
  console.log("DB Color after conversion:", dbColor);
  const extraData = {
    fixationShiftAngle: fixationShiftAngle,
    speed: kineticExtraData?.speed ?? 0.507,
    testTypeCircleAngle,
    numberOfStimuliStarts,
  } as KineticTestCustomConfiguration;
  const isCalibrationNeeded = config?.eyeConfiguration?.isCalibrationNeeded ?? true;
  const virtualAssistanceConfig = config?.virtualAssistanceConfig;
  const isBackToBackEyeTest = config?.eyeConfiguration?.isBackToBackEyeTest ?? false;

  return {
    name,
    testType,
    testBehaviour,
    eye,
    isBackToBackEyeTest,
    startWithCalibrationScene: config.startWithCalibrationScene ?? false,
    autoPauseForGaze,
    twoScreenFixation,
    blindSpotTesting,
    blindSpotMonitoringEnabled,
    fovealThresholdTestingEnabled,
    enablePausingTheTestWithButton,
    defaultMustWait,
    showTutorial,
    fixationSize,
    stimuliSize,
    testPattern,
    dbColor,
    extraData,
    isCalibrationNeeded,
    virtualAssistanceConfig,
    isPressToStartEnabled: config.isPressToStartEnabled,
    isEyeCalibrationNeeded: isCalibrationNeeded,
  };
}

function MapNonVFATestConfigurationToPayload(
  config: TestConfiguration | TestProgressConfiguration,
  readingAssessmentPageChangeInteraction?: ReadingAssessmentPageChangeInteraction,
  testName?: TestCategory,
): CompleteNonVFATestConfigPayload {
  if (!config) {
    throw new Error("Test configuration is undefined or null");
  }
  const configTestName = config.name ?? testName;
  if (configTestName === TestCategory.VisualField) {
    throw new Error("Provided configuration is not for a Non-VFA test");
  }

  const eyeConfiguration = config?.eyeConfiguration;
  const virtualAssistantConfig = config?.virtualAssistanceConfig;
  const fdtBehaviourType = (config?.extraData as FDTTestCustomConfiguration)?.behaviourType;
  const fdtConstants = (config?.extraData as FDTTestCustomConfiguration)?.constants;
  const showTutorial =
    (config?.extraData as ColorVisionCustomConfiguration | VisualAcuityCustomConfiguration)
      ?.showTutorial ?? false;
  const kineticExtraData = (config?.extraData as KineticTestCustomConfiguration) ?? null;
  const kineticGoldmannSize = kineticExtraData?.stimuliSize;
  const kineticTestPattern = kineticExtraData?.testPattern;

  const extraData = config?.extraData
    ? convertExtraDataForBackend(
        config.extraData as TestSpecificConfig,
        configTestName,
        readingAssessmentPageChangeInteraction,
      )
    : null;
  const cptConfigMode: CPTConfigMode | null =
    configTestName === TestCategory.ContinuousPerformance
      ? ((config.extraData as CPTCustomConfiguration | undefined)?.cptConfigMode ?? null)
      : null;
  const kineticDBColor = kineticExtraData?.dbColor;
  const kineticSpeed = kineticExtraData?.speed;
  const eye = getEyeTypeForPayload(eyeConfiguration?.eyeType);
  return {
    name: configTestName,
    testType: config.testType,
    behaviourType: fdtBehaviourType,
    constants: fdtConstants,
    eye: eye,
    isBackToBackEyeTest: eyeConfiguration?.isBackToBackEyeTest ?? false,
    startWithCalibrationScene: config.startWithCalibrationScene ?? false,
    isCalibrationNeeded: eyeConfiguration?.isCalibrationNeeded ?? true,
    autoPauseForGaze: eyeConfiguration?.autoPauseForGaze,
    goldmannSize: kineticGoldmannSize,
    testPattern: kineticTestPattern,
    extraData: extraData as ConfigExtraData | null,
    cptConfigMode: cptConfigMode,
    testLanguage: convertTestLanguageForBackend(config.testLanguage),
    dbColor: kineticDBColor,
    speed: kineticSpeed,
    virtualAssistanceConfig: (virtualAssistantConfig ?? null) as VirtualAssistantConfig | null,
    isPressToStartEnabled:
      configTestName !== undefined && TESTS_WITH_PRESS_TO_START_DISABLED.has(configTestName)
        ? false
        : config.isPressToStartEnabled,
    showTutorial,
    isEyeCalibrationNeeded: eyeConfiguration?.isCalibrationNeeded ?? true,
  };
}

function convertTestLanguageForBackend(testLanguage: TestLanguage): string | null {
  if (testLanguage === TestLanguage.NOT_SPECIFIED) {
    return null;
  }
  switch (testLanguage) {
    case TestLanguage.English: {
      return "ENGLISH";
    }
    case TestLanguage.Turkish: {
      return "TURKISH";
    }
    case TestLanguage.Arabic: {
      return "ARABIC";
    }
  }
}
function convertExtraDataForBackend(
  extraData: TestSpecificConfig,
  testCategory: TestCategory,
  readingAssessmentPageChangeInteraction?: ReadingAssessmentPageChangeInteraction,
): ConfigExtraData | TestSpecificConfig | null {
  switch (testCategory) {
    case TestCategory.OcularMotility: {
      return extraData as OMTestCustomConfiguration as OcularMotilityTestCustomConfigLegacy;
    }
    case TestCategory.ContinuousPerformance: {
      return extraData as CPTCustomConfiguration as CustomCPTConfigLegacy;
    }
    case TestCategory.Dyslexia: {
      return extraData as DYCustomConfiguration as CustomDyslexiaConfigLegacy;
    }
    case TestCategory.WisconsinCardSorting: {
      return extraData;
    }
    case TestCategory.Dyscalculia: {
      const dyscalculiaExtraData = extraData as Omit<
        DyscalculiaConfigPayload,
        "taskInstructionVideos"
      >;
      const dyscalculiaPayload: DyscalculiaConfigPayload = {
        ...dyscalculiaExtraData,
        taskInstructionVideos: DYSCALCULIA_TASK_INSTRUCTION_VIDEOS,
      };
      return dyscalculiaPayload;
    }
    case TestCategory.ReadingAssessment: {
      const readingAssessmentExtraData = extraData as ReadingAssessmentCustomConfiguration;
      const readingAssessmentPayload: ReadingAssessmentConfigPayload = {
        ...readingAssessmentExtraData,
        ...(readingAssessmentPageChangeInteraction
          ? { pageChangeInteraction: readingAssessmentPageChangeInteraction }
          : {}),
        ...(readingAssessmentExtraData.showComprehensionQuestions
          ? { comprehensionInstructionVideo: READING_ASSESSMENT_COMPREHENSION_INSTRUCTION_VIDEO }
          : {}),
      };
      return readingAssessmentPayload;
    }
    case TestCategory.Pupillometry: {
      return extraData as PupillometryCustomConfiguration as PupillometryCustomConfigurationLegacy;
    }
    case TestCategory.Kinetic: {
      const kineticExtraData = extraData as KineticTestCustomConfiguration;
      const { testTypeCircleAngle, numberOfStimuliStarts } =
        resolveKineticTestTypeValues(kineticExtraData);
      const fixationShiftAngle = getFixationShiftAngle(
        kineticExtraData?.testPattern,
        testTypeCircleAngle,
        numberOfStimuliStarts,
      );
      return {
        fixationShiftAngle: fixationShiftAngle,
        speed: kineticExtraData?.speed ?? 0.507,
        testTypeCircleAngle,
        numberOfStimuliStarts,
      } as KineticTestCustomConfiguration;
    }
    case TestCategory.VisualField:
    case TestCategory.VisualAcuity:
    case TestCategory.FDT: {
      console.log("Extra data field of this test is not to be converted: ", testCategory);
      return null;
    }
    case TestCategory.ColorVision: {
      const { showTutorial: _showTutorial, ...colorVisionExtraData } =
        extraData as ColorVisionCustomConfiguration;
      return colorVisionExtraData;
    }
  }
  console.warn(
    "An unhandled test category was provided for extraData conversion:",
    testCategory,
    "returning original extraData.",
  );
  // Feel free to remove this warning if it gets too noisy.
  return extraData;
}

function getEyeTypeForPayload(
  eyeType: EyeType,
): EyeType.BothEye | EyeType.LeftEye | EyeType.RightEye {
  if (eyeType === EyeType.RightThenLeft) {
    return EyeType.RightEye;
  }
  return eyeType;
}
